/**
 * 分批请求工具函数（串行请求）
 * @param {Object} postData - 需要分批处理的任务数据
 * @param {string} batchPropName - 用于判断分批的数据字段名称
 * @param {Function} requestFn - 实际发送请求的函数，接收当前批次的任务并返回 Promise
 * @param {number} batchSize - 每批的任务数量，默认为 5
 * @param {Function} onProgress - 进度回调函数，接收当前进度（0-100）
 * @returns {Promise<void>} - 返回一个 Promise，所有批次请求完成后 resolve
 */
export function batchRequest<T extends Record<string, any>>(
  postData: T,
  batchPropName: keyof T,
  requestFn: (data: T) => Promise<void>,
  batchSize = 5,
  onProgress?: (progress: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    // 确保 batchPropName 对应的值是字符串
    const taskArray = String(postData[batchPropName]).split(',')

    // 按 batchSize 切割成多个批次
    const taskBatches: string[] = []
    for (let i = 0; i < taskArray.length; i += batchSize) {
      const batch = taskArray.slice(i, i + batchSize)
      const batchString = batch.join(',')
      taskBatches.push(batchString)
    }

    const totalBatches = taskBatches.length
    let completedBatches = 0

    // 如果没有任务，直接 resolve
    if (taskBatches.length === 0) {
      resolve()
      return
    }

    // 定义一个异步函数来处理串行请求
    const executeBatches = async () => {
      try {
        for (const batch of taskBatches) {
          // 更新当前批次的 batchPropName
          postData[batchPropName] = batch as T[keyof T]
          await requestFn(postData) // 等待当前批次请求完成
          completedBatches++
          // 计算并触发进度回调
          const progress = Math.floor((completedBatches / totalBatches) * 100)
          if (onProgress) {
            onProgress(progress)
          }
        }
        // 所有批次完成后 resolve
        resolve()
      } catch (error) {
        // 如果某个批次失败，直接 reject
        reject(error)
      }
    }

    // 执行异步函数
    executeBatches()
  })
}
